import { z } from "zod";
import { LocaleSchema } from "./locale";

/**
 * Canonical email identity used at every external boundary.
 *
 * Deliberately limited to whitespace trimming + case folding. Provider-specific
 * aliases such as Gmail dots and plus tags remain distinct identities.
 */
export const EmailSchema = z.string().trim().toLowerCase().email();

export function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

/** Permission codes */
export const PermissionCodeSchema = z.enum([
  "view",
  "manage_ai",
  "approve",
  "manage_team",
  "manage_connections",
]);
export type PermissionCode = z.infer<typeof PermissionCodeSchema>;

/**
 * Workspace membership role (ADP-167 main/sub-account model, ADP-180 admin):
 * `owner` is the main account (unique per workspace), `admin` a sub-account
 * promoted from the ops backstage (full permissions incl. manage_team, but not
 * the owner), `member` a plain sub-account. Permission codes are derived from
 * this role + module grants in code (`permissionsForMember`), never stored
 * per-member.
 */
export const WorkspaceRoleSchema = z.enum(["owner", "admin", "member"]);
export type WorkspaceRole = z.infer<typeof WorkspaceRoleSchema>;

/** Shop member role — the same owner/member axis as the workspace role. */
export const MemberRoleSchema = WorkspaceRoleSchema;
export type MemberRole = z.infer<typeof MemberRoleSchema>;

/** Shop member status */
export const MemberStatusSchema = z.enum(["active", "removed"]);
export type MemberStatus = z.infer<typeof MemberStatusSchema>;

/** User status */
export const UserStatusSchema = z.enum(["active", "disabled"]);
export type UserStatus = z.infer<typeof UserStatusSchema>;

/** User shape returned from auth endpoints */
export const authUserSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  name: z.string().nullable(),
  avatar_url: z.string().nullable(),
  // ADP-66: the User's active Workspace (server-sticky per-user). Optional until
  // the auth rewire populates it; absent in legacy responses (frontend tolerates).
  active_workspace_id: z.string().uuid().nullable().optional(),
  /** 未设置时为 null，前端按浏览器语言检测并 fallback en-US */
  locale: LocaleSchema.nullable(),
  shops: z.array(
    z.object({
      shop_id: z.string().uuid(),
      shop_name: z.string(),
      role: MemberRoleSchema,
      permissions: z.array(PermissionCodeSchema),
    }),
  ),
});

export type AuthUser = z.infer<typeof authUserSchema>;

/** Dev login request (development only) */
export const devLoginRequestSchema = z.object({
  email: EmailSchema,
});
